import { TinyEmitter } from "@expert/event-bus";
import type { ExpertUniversityEvents } from "@expert/expert-university";
import { useExpertUniversityStore } from "@expert/expert-university";
import type { Partner } from "@expert/shared-types";
import { env } from "@/config";
import type { AgentActivity, ChatMessage } from "@/sdk";
import { AgentSdkBase } from "@/sdk";
import { userCache } from "@/shared-utils/auth/userCache";
import { createTrainingSession } from "./api/requests";
import { createSimulatedVoiceTaskForExpertUniversity, ExpertUniversitySimulatedVoiceTask } from "./SimulatedVoiceTask";
import { TrainingSessionChallengeStatusEnum } from "./types";

export default class ExpertUniversitySdk extends AgentSdkBase<ExpertUniversitySimulatedVoiceTask> {
    public override agentActivities: readonly AgentActivity[];
    public override hasActivitySelfManagement?: boolean | undefined;
    private expertUniversitySdkEventBus = new TinyEmitter<ExpertUniversityEvents>(
        "expert-university-event-fired",
        false,
    );
    private mediaRecorder: MediaRecorder | null = null;
    private audioStream: MediaStream | null = null;
    private appId: string;

    private partner: Set<Partner> = new Set();

    constructor(partner: Partner | undefined) {
        super();
        if (partner) {
            this.partner.add(partner);
        }
        this.agentActivities = ["Default", "Training", "Available", "Offline"] as AgentActivity[];
        useExpertUniversityStore.getState().setEventBus(this.expertUniversitySdkEventBus);
        //event listeners
        this.expertUniversitySdkEventBus.on("websocket-opened", (event) => {
            if (event.websocketUrl) {
                void this.openWebsocketConnection(event.websocketUrl);
            }
        });
        this.expertUniversitySdkEventBus.on("websocket-closed", (_event) => {
            console.info("Websocket connection closed");
            this.resetWebsocket();
        });
        this.expertUniversitySdkEventBus.on("audio-event-received", (event) => {
            this.convertStringToAudioBlob(event.audioBlob);
            this.processAudioEvent();
        });
        this.expertUniversitySdkEventBus.on("start-simulation-training", (event) => {
            if (event.profileId) {
                void this.startSimulationTraining(event.profileId);
            }
        });
        this.appId = crypto.randomUUID();
    }
    public getSdkEventBus(): TinyEmitter<ExpertUniversityEvents> {
        return this.expertUniversitySdkEventBus;
    }
    public override canHandleTaskType(task: ExpertUniversitySimulatedVoiceTask): boolean {
        return task instanceof ExpertUniversitySimulatedVoiceTask && task.profileId !== undefined;
    }

    public override acceptTask(): Promise<void> {
        return Promise.resolve();
    }

    public override get offlineAgentActivity(): AgentActivity {
        return "Offline" as AgentActivity;
    }

    public override get availableAgentActivity(): AgentActivity {
        return "Available" as AgentActivity;
    }

    public override get defaultAgentActivity(): AgentActivity {
        return "Default" as AgentActivity;
    }

    public override getPartners(): ReadonlySet<Partner> {
        return this.partner;
    }

    public override isAgentActivitySelectable(): boolean {
        return true;
    }

    // TODO figure out how to get the agent activity
    public override getAgentActivity(): AgentActivity {
        return "Training" as AgentActivity;
    }

    public override getAgentSite(): string | undefined {
        return undefined;
    }

    public override async hangupCall(task: ExpertUniversitySimulatedVoiceTask): Promise<void> {
        useExpertUniversityStore.getState().getWebsocket?.()?.close();
        this.onHangupCall(task);
        await this.onTaskComplete(task, "TrainingCompleted");
        this.expertUniversitySdkEventBus.emit("trigger-evaluation-results", {
            sessionId: task.sessionId,
            profileId: task.profileId,
        });
    }

    public override muteCall(task: ExpertUniversitySimulatedVoiceTask, shouldMute: boolean): Promise<void> {
        task.isMuted = shouldMute;
        this.onMuteCall(task, shouldMute);
        this.audioStream?.getAudioTracks().forEach((track) => {
            track.enabled = !shouldMute;
        });
        return Promise.resolve();
    }

    public override setAgentActivity(): Promise<void> {
        return Promise.resolve();
    }

    //Private methods
    private startSimulationTraining = async (profileId: string) => {
        useExpertUniversityStore.getState().getWebsocket?.()?.close();
        const task = createSimulatedVoiceTaskForExpertUniversity(profileId);
        useExpertUniversityStore.getState().setProfileId(task.profileId);
        await this.onTaskCreated(task);
        await this.createTrainingSession(task.sessionId, task.profileId);
    };

    private createTrainingSession = async (sessionId: string, profileId: string) => {
        await createTrainingSession({
            SessionID: sessionId,
            AppId: this.appId,
            ChallengeId: profileId,
            ChallengeStatus: TrainingSessionChallengeStatusEnum.NOT_COMPLETE,
            EmployeeId: userCache.employeeId,
            SessionStartTime: new Date().toISOString(),
            Environment: env.isProdMode ? "prod" : "dev",
        });
    };

    private resetWebsocket(): void {
        this.mediaRecorder?.stream.getTracks().forEach((track) => {
            track.stop();
        });
        this.mediaRecorder?.stop();
        useExpertUniversityStore.getState().resetEvents();
    }

    private openWebsocketConnection(websocketURL: string): void {
        console.info("Websocket connection opened:\n", websocketURL);
        if (websocketURL) {
            void this.initMediaRecorder();
        }
    }

    private async handleDataAvailable(event: BlobEvent): Promise<void> {
        if (event.data.size > 0) {
            const buffer = await event.data.arrayBuffer();
            useExpertUniversityStore.getState().sendMessage?.(buffer);
        }
    }

    private async initMediaRecorder() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.audioStream = stream;
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorder.ondataavailable = (event) => void this.handleDataAvailable(event);
            mediaRecorder.start(1024); // Buffer size
            this.mediaRecorder = mediaRecorder;
            console.info(">> Microphone access granted");
            return true;
        } catch (error) {
            console.error(">> Microphone access denied", error);
            return false;
        }
    }

    private convertStringToAudioBlob(data: string): void {
        const cleanBase64 = data
            .trim()
            .replace(/^data:.*?base64,/, "") // remove data URI prefix if present
            .replace(/[^A-Za-z0-9+/=]/g, "");
        const decodedBase64Data = atob(cleanBase64);
        const byteNumbers = Array.from(decodedBase64Data).map((char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], { type: "audio/wav" });
        const url = URL.createObjectURL(audioBlob);
        const audio = new Audio(url);
        useExpertUniversityStore.getState().audioElements?.push(audio);
    }

    private processAudioEvent(): void {
        const { isBotPlaying, audioElements, setIsBotPlaying } = useExpertUniversityStore.getState();
        if (isBotPlaying || audioElements?.length === 0) {
            return;
        }
        const audio = audioElements?.shift();
        if (!audio) {
            return;
        }
        setIsBotPlaying(true);
        audio.onended = () => {
            URL.revokeObjectURL(audio.src);
            setIsBotPlaying(false);
            this.processAudioEvent();
        };

        audio.onerror = () => {
            console.error("Error playing audio element");
            URL.revokeObjectURL(audio.src);
            setIsBotPlaying(false);
            this.processAudioEvent();
        };

        audio.play().catch((error) => {
            console.error("Error playing audio element:", error);
            setIsBotPlaying(false);
            this.processAudioEvent();
        });
    }

    // Not implemented methods

    public override updateAgentAttributes(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override rejectTask(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override wrapupTask(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override completeTask(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override cancelTask(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override sendToSurvey(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override holdCall(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override resumeCall(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override sendDtmfDigits(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override redirectCallToTFN(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override redirectCallToSIP(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override addConferenceParticipant(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override updateParticipant(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override removeParticipant(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override removePendingParticipant(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override leaveConference(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override endConference(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override resetConferenceState(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override getMessages(): Promise<ChatMessage[]> {
        throw new Error("Method not implemented.");
    }

    public override sendMessage(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public override advanceReadStatus(): Promise<number> {
        throw new Error("Method not implemented.");
    }

    protected override createCustomerCallbackForTask(): Promise<string> {
        throw new Error("Method not implemented.");
    }

    protected override createOutboundCall(): Promise<string> {
        throw new Error("Method not implemented.");
    }
}
